import React, { useEffect } from 'react';
import { Form, Input, Select, Button, Card, Typography, Space, Spin, List, Popconfirm, Tag } from 'antd';
import { DeleteOutlined, HistoryOutlined, ClearOutlined } from '@ant-design/icons';

const { TextArea } = Input;
import { useDataGenerator } from './useDataGenerator';
import ErrorBoundary from './ErrorBoundary';
import config from '../../config';
import './styles.css';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const DataGeneratorInner = () => {
  const [form] = Form.useForm();
  const {
    loading,
    loadingCategories,
    selectedCategory,
    availableMedications,
    categoryOptions,
    loadingStates,
    selectedState,
    stateOptions,
    medicationOptions,
    currentCategoryMifs,
    lastResponse,
    handleCategoryChange,
    handleStateChange,
    handleSubmit,
    resetState,
    loadMifFromHistory,
    clearMifHistory,
    clearCategoryUsage,
  } = useDataGenerator();

  const onCategoryChange = (categoryName) => {
    handleCategoryChange(categoryName, form);
  };

  const onStateChange = (stateName) => {
    handleStateChange(stateName, form);
  };

  const onSubmit = async (values) => {
    await handleSubmit(values);
    // Don't reset form on successful submit - let user see the response
  };

  const onReset = () => {
    resetState(form);
  };

  const getEvaluationStatusTag = (status) => {
    const statusConfig = {
      'passed': { color: 'success', text: 'Passed' },
      'failed': { color: 'error', text: 'Failed' },
      'not-evaluated': { color: 'default', text: 'Not Evaluated' },
    };
    
    const config = statusConfig[status] || statusConfig['not-evaluated'];
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // Set default category value when selectedCategory is loaded from localStorage
  useEffect(() => {
    if (selectedCategory) {
      form.setFieldsValue({ telemedicinecategory: selectedCategory.name });
    }
  }, [selectedCategory, form]);

  useEffect(() => {
    if (selectedState) {
      form.setFieldsValue({ state: selectedState.name });
    }
  }, [selectedState, form]);

  // Show loading spinner while fetching data
  if (loadingCategories || loadingStates) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Spin size="large" />
        <p style={{ marginTop: '20px' }}>Loading categories and states...</p>
      </div>
    );
  }

  return (
    <div className="data-generator-container">
      <Card>
        <Title level={2}>Clinical Eligibility Validator Test Case Generator</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
          Generate a test case with complete patient data. Only available in dev/stage/local environments.
          <br />
          Provide a JavaScript object (or JSON) containing patient data. The <code>mif</code> field must be a stringified JSON array.
          <br />
          The <code>height</code> field should be total inches (e.g., 68 for 5'8'').
          <br />
          Client will automatically be set to "Rex MD".
        </Text>

        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
        >
          <Form.Item
            label="Telemedicine Category"
            name="telemedicinecategory"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select
              showSearch
              placeholder="Select a category"
              onChange={onCategoryChange}
              options={categoryOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            label="State"
            name="state"
            rules={[{ required: true, message: 'Please select a state' }]}
          >
            <Select
              showSearch
              placeholder="Select a state"
              onChange={onStateChange}
              options={stateOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            label="Medication"
            name="medication"
            rules={[{ required: true, message: 'Please select a medication' }]}
          >
            <Select
              showSearch
              placeholder={selectedCategory ? "Select a medication" : "Select a category first"}
              disabled={loading || !selectedCategory || availableMedications.length === 0}
              options={medicationOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            />
          </Form.Item>

          {availableMedications.length === 0 && selectedCategory && (
            <Text type="warning" style={{ display: 'block', marginBottom: '16px' }}>
              No medications with NDC codes found for this category.
            </Text>
          )}

          {currentCategoryMifs.length > 0 && (
            <Card 
              size="small" 
              title={
                <Space>
                  <HistoryOutlined />
                  <span>Patient Data History for {selectedCategory?.name}</span>
                </Space>
              }
              style={{ marginBottom: '16px' }}
              extra={
                <Popconfirm
                  title="Clear all patient data history?"
                  onConfirm={clearMifHistory}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button 
                    size="small" 
                    type="link" 
                    danger 
                    icon={<DeleteOutlined />}
                  >
                    Clear All
                  </Button>
                </Popconfirm>
              }
            >
              <List
                size="small"
                dataSource={currentCategoryMifs}
                renderItem={(item) => (
                  <List.Item
                    style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}
                    actions={[
                      <Button 
                        type="primary"
                        size="small"
                        onClick={() => loadMifFromHistory(item, form)}
                      >
                        Load
                      </Button>
                    ]}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#666' }}>
                          {dayjs(item.timestamp).format("MM/DD/YYYY hh:mm A")}
                        </span>
                        {getEvaluationStatusTag(item.evaluationStatus)}
                      </div>
                      <div style={{ maxHeight: '60px', overflow: 'hidden', fontSize: '12px', color: '#999' }}>
                        {item.mif.substring(0, 150)}...
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            </Card>
          )}

          <Form.Item
            label="Patient Data (Object)"
            name="patientData"
            rules={[
              { required: true, message: 'Please enter patient data' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.reject('Patient data is required');
                  
                  let parsed;
                  try {
                    // Try JSON.parse first
                    parsed = JSON.parse(value);
                  } catch (jsonError) {
                    // If JSON.parse fails, try to evaluate as JavaScript object literal
                    try {
                      // Wrap in parentheses and use Function constructor for safer eval
                      parsed = new Function('return (' + value + ')')();
                    } catch (evalError) {
                      return Promise.reject('Must be a valid JavaScript object or JSON');
                    }
                  }
                  
                  // Check if it's an object
                  if (typeof parsed !== 'object' || Array.isArray(parsed)) {
                    return Promise.reject('Patient data must be an object');
                  }
                  
                  // Validate required fields
                  const requiredFields = ['mif', 'age', 'gender', 'weight', 'height'];
                  const missingFields = requiredFields.filter(field => !parsed[field]);
                  
                  if (missingFields.length > 0) {
                    return Promise.reject(`Missing required fields: ${missingFields.join(', ')}`);
                  }
                  
                  // Validate MIF is a stringified array
                  if (typeof parsed.mif !== 'string') {
                    return Promise.reject('mif field must be a JSON string');
                  }
                  
                  // Try to parse the MIF string to validate it's a valid array
                  try {
                    const mifArray = JSON.parse(parsed.mif);
                    if (!Array.isArray(mifArray)) {
                      return Promise.reject('mif must be a stringified JSON array');
                    }
                    if (mifArray.length === 0) {
                      return Promise.reject('mif array cannot be empty');
                    }
                  } catch (e) {
                    return Promise.reject('mif must be valid stringified JSON');
                  }
                  
                  // Validate age
                  const age = parseInt(parsed.age, 10);
                  if (isNaN(age) || age < 18 || age > 120) {
                    return Promise.reject('age must be a number between 18 and 120');
                  }
                  
                  // Validate gender
                  if (!['M', 'F', 'O', 'male', 'female'].includes(parsed.gender)) {
                    return Promise.reject('gender must be M, F, O, male, or female');
                  }
                  
                  return Promise.resolve();
                }
              }
            ]}
            tooltip="Complete patient data object. The 'mif' field must be a stringified JSON array. The 'height' field should be total inches (e.g., 68 for 5'8''). Can be JSON or JavaScript object literal."
          >
            <TextArea
              rows={8}
              placeholder={`{\n  mif: "[{\\"q\\":[\\"Question?\\"],\\"a\\":[\\"Answer\\"],\\"p\\":[null],\\"slug\\":[null]}]",\n  medications: "JARDIANCE",\n  allergies: "None",\n  bloodPressure: "120/80",\n  age: 45,\n  gender: "M",\n  weight: "165 lbs",\n  height: 68\n}`}
              style={{ fontFamily: 'monospace', fontSize: '12px' }}
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            label={
              <Space>
                <span>API Response</span>
                {lastResponse && (
                  <Button 
                    size="small" 
                    type="link"
                    onClick={() => navigator.clipboard.writeText(JSON.stringify(lastResponse, null, 2))}
                  >
                    Copy to Clipboard
                  </Button>
                )}
              </Space>
            }
          >
            <TextArea
              key={lastResponse ? 'with-response' : 'no-response'}
              value={lastResponse ? JSON.stringify(lastResponse, null, 2) : ''}
              readOnly
              rows={12}
              placeholder="API response will appear here after case generation..."
              style={{ 
                fontFamily: 'monospace', 
                fontSize: '12px',
                backgroundColor: '#f5f5f5',
                color: '#000'
              }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading} 
                size="large"
                disabled={loading || !selectedCategory || availableMedications.length === 0}
              >
                Generate Test Case
              </Button>
              <Button onClick={onReset} size="large" disabled={loading}>
                Reset Form
              </Button>
              <Popconfirm
                title="Clear category usage statistics?"
                description="This will reset the category sort order."
                onConfirm={clearCategoryUsage}
                okText="Yes"
                cancelText="No"
              >
                <Button icon={<ClearOutlined />} size="large" disabled={loading}>
                  Clear Usage History
                </Button>
              </Popconfirm>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

const DataGenerator = () => {
  return (
    <ErrorBoundary>
      <DataGeneratorInner />
    </ErrorBoundary>
  );
};

export default DataGenerator;
